import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { cn } from './cn';

/**
 * Badge — a compact status or category marker.
 *
 * Square-cornered, not a pill. A fully rounded chip reads as a tag you could
 * dismiss; the state of a vehicle is not that, and at this size the pill shape
 * was wider than the word inside it. It takes the same 6px every other corner
 * in the app takes — see the note on `--radius` in `globals.css` — so a badge
 * in a table cell agrees with the cell, the button beside it and the card
 * around all three.
 *
 * `success` / `warning` / `info` map to the semantic tokens this project
 * already defines, so state colour never gets hand-picked at the call site.
 *
 * They are drawn as a tinted chip — the signal colour for the text, a wash of
 * itself behind. Filling the chip with the signal colour instead forces a text
 * colour that can only work in one theme: the previous `warning` variant put
 * black on a mustard that is dark in light mode (1.9:1) and light in dark mode.
 * The tokens are measured against `--card`, and a 12% wash of a colour on the
 * card barely moves that ratio, so both modes clear AA from one definition.
 */
const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-md ' +
    'border border-transparent px-1.5 py-0.5 text-2xs font-medium transition-colors ' +
    '[&>svg]:pointer-events-none [&>svg]:size-3',
  {
    /* FleetSight's chip: no border, the tone washed in behind its own text. */
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground',
        secondary: 'bg-muted text-muted-foreground',
        destructive: 'bg-error/12 text-error',
        outline: 'border-border text-foreground',
        success: 'bg-success/12 text-success',
        warning: 'bg-warning/15 text-warning',
        info: 'bg-info/12 text-info',
        accent: 'bg-accent/12 text-accent',
      },
    },
    defaultVariants: { variant: 'default' },
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';
  return (
    <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
